import React from "react";
import {
  Container,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Typography,
  Box,
} from "@mui/material";
import Cards from "./Cards";
import "./Home.css";
import InfoPopup from "./InfoPopup";
import { analytics } from "../firebase/firebaseClient";
import { logEvent } from "firebase/analytics";

const dashboards = [
  {
    id: 1,
    name: "Sales Dashboard",
    img: "/Gemini_Generated_Image_b2c5g6b2c5g6b2c5.png",
    link: "https://sales-insights.haqdarshak.com",
    info: "Track citizens impacted, applications submitted, MSMEs reached, HDs trained, and benefits unlocked with filters by project, HD, or citizen details",
  },
  {
    id: 2,
    name: "Project 360 Dashboard",
    img: "/Gemini_Generated_Image_ws6zixws6zixws6z.png",
    link: "https://project360.insights.haqdarshak.com",
    info: "Explore project milestones, KPIs, and high-level summaries across all active projects.",
  },
  {
    id: 3,
    name: "Achievement Dashboard",
    img: "/Gemini_Generated_Image_wdol4gwdol4gwdol.png",
    link: "https://usersreport-insights.haqdarshak.com",
    info: "View daily, weekly, and monthly performance by PID, organization, date, or manager. Drill down to individual-level data",
  },
  {
    id: 4,
    name: "DFL Dashboard",
    img: "/Gemini_Generated_Image_1m8kuq1m8kuq1m8k.png",
    link: "https://dfl-insights.haqdarshak.com",
    info: "Get an overview of camps, institutes, trainers, and participants under the DFL initiative.",
  },
];

const additionalImages = [
  {
    img: "/a.png",
    text: "5000+ Welfare Schemes",
    extraText:
      "Our platform provides access to over 5,000 central, state, and local welfare schemes...",
  },
  {
    img: "/5.png",
    text: "Haqdarshak Agents",
    extraText:
      "More than 51,000 Haqdarshak field agents are trained and deployed across India...",
  },
  {
    img: "/3.png",
    text: "INR 18,636 Cr+ Benefits Unlocked",
    extraText:
      "We’ve facilitated the unlocking of benefits worth over INR 18,636 crore,...",
  },
  {
    img: "/4.png",
    text: "Yojana Kendra",
    extraText:
      "Yojana Kendras are community-run service centers providing end-to-end support...",
  },
];

const cardSx = {
  background: "#ffffff",
  border: "1.5px solid #e2e8f0",
  borderRadius: "16px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  transition: "box-shadow 0.2s ease, transform 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(0,0,0,0.07)",
    transform: "translateY(-2px)",
  },
};

const Home = () => {
  return (
    <Box className="home-container">
      <Container maxWidth="lg" sx={{ textAlign: "center", color: "#0f172a", paddingTop: "50px" }}>
        <Typography variant="h2" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: "2.2rem", md: "3rem" } }}>
          Welcome to Haqdarshak Insights
        </Typography>
        <Typography variant="h5" sx={{ mb: 5, color: "#334155", fontWeight: 500 }}>
          Empowering communities with data-driven insights
        </Typography>
      </Container>

      <Cards />

      {/* Highlights Section Header */}
      <Container maxWidth="lg" sx={{ textAlign: "center", mt: 5 }}>
        <Typography variant="h3" sx={{ fontWeight: 800, color: "#0f172a", mb: 3, fontSize: { xs: "1.8rem", md: "2.4rem" } }}>
          Our Key Highlights
        </Typography>
        <Typography variant="subtitle1" sx={{ color: "#64748b", mb: 4 }}>
          Discover the impact we're making through welfare schemes, agents, and services
        </Typography>
      </Container>

      {/* Highlight Card Grid */}
      <Container maxWidth="lg">
        <Grid container spacing={3} justifyContent="center">
          {additionalImages.map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card className="highlight-card" sx={cardSx}>
                <Box
                  className="highlight-card-media"
                  sx={{ backgroundImage: `url(${process.env.PUBLIC_URL}${item.img})` }}
                />
                <CardContent sx={{ textAlign: "left" }}>
                  <Typography sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem", mb: 1 }}>
                    {item.text}
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: "0.85rem" }}>
                    {item.extraText}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Info Popup Section */}
      <Container maxWidth="lg" sx={{ textAlign: "center", mt: 5 }}>
        <InfoPopup />
      </Container>

      {/* Dashboards Section */}
      <Container maxWidth="lg" sx={{ textAlign: "center", mt: 5 }}>
        <Typography variant="h3" sx={{ fontWeight: 800, color: "#0f172a", mb: 3, fontSize: { xs: "1.8rem", md: "2.4rem" } }}>
          Explore Our Dashboards
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          {dashboards.map((dash) => (
            <Grid item xs={12} sm={6} md={3} key={dash.id}>
              <CardActionArea
                onClick={() => {
                  if (analytics) {
                    logEvent(analytics, "dashboard_clicked", {
                      dashboard_name: dash.name,
                      dashboard_id: dash.id,
                      time: new Date().toISOString(),
                    });
                  }

                  if (dash.link) {
                    window.open(dash.link, "_blank");
                  }
                }}
                sx={{ borderRadius: "16px" }}
              >
                <Card
                  className="dashboard-card"
                  sx={{
                    ...cardSx,
                    height: 260,
                    width: "100%",
                    maxWidth: 300,
                    display: "flex",
                    flexDirection: "column",
                    margin: "0 auto",
                    overflow: "hidden",
                  }}
                >
                  <Box className="image-container">
                    <CardMedia
                      component="img"
                      image={`${process.env.PUBLIC_URL}${dash.img}`}
                      alt={dash.name}
                      className="dashboard-img"
                    />
                    <Box className="hover-info">
                      <Typography variant="body2">{dash.info}</Typography>
                    </Box>
                  </Box>
                  <CardContent sx={{ backgroundColor: "#ffffff", borderTop: "1px solid #f1f5f9" }}>
                    <Typography
                      variant="body1"
                      align="center"
                      sx={{ fontSize: "14px", fontWeight: 700, color: "#0f172a" }}
                    >
                      {dash.name}
                    </Typography>
                  </CardContent>
                </Card>
              </CardActionArea>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Home;
